import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';
import { endOfDay, startOfDay } from 'date-fns';

import {
  fileDownload,
  formatArchiveOptions,
  formatPayloadDate,
  getDateRangeFromLabel,
  getMaskedValue,
  maskExportRows,
  MASKED_NUMBER_FIELDS
} from '../../utils/common';
import Pagination from '../../components/form/Pagination';
import FormSelect from '../../components/form/FormSelect';
import AdminHeader from '../../components/header/AdminHeader';
import FormDateInput from '../../components/form/FormDateInput';
import whatsappIcon from '../../assets/images/icons/whatsapp.svg';

import { callHistoryGet } from '../../store/slices/callHistorySlice';
import { archiveOptionsGet } from '../../store/slices/tenantSlices/dailyReportSlice';
import CallexLoader from '../../components/CallexLoader';
import { setIsDialNumber } from '../../store/slices/dialSlice';
import FormInput from '../../components/form/FormInput';
import { useUserDetails } from '../../store/slices/authSlice';
import useWhatsappHandoff from '../../hooks/useWhatsappHandoff';
import { setCallingModalOpen, useTimezone } from '../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import config from '../../config';

const CallHistory = () => {
  const timezone = useTimezone();
  const dispatch = useDispatch();
  const user = useUserDetails();
  const handleWhatsappClick = useWhatsappHandoff();
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  // Starts true because the first search waits for the archive options to resolve -
  // without this the empty state flashes before the loader appears.
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [paginationDetails, setPaginationDetails] = useState({ page: 1, total: 0, limit: 10 });
  const [archiveOptions, setArchiveOptions] = useState([]);
  const hasAutoSearchedRef = useRef(false);
  const [headerFields, setHeaderFields] = useState({
    default: [],
    crm_fields: []
  });

  const today = toZonedTime(new Date(), timezone);
  const isNumberMaskingEnabled = user?.process_details?.numberMasking === 'true';

  const initialValues = {
    page: 1,
    limit: 10,
    type: 'all',
    destination_number: '',
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required'),
    type: Yup.string().required('Type is required')
  });

  // `range` lets the first automatic search use the month range as soon as it is
  // derived, without waiting for setFieldValue to land in `values`.
  const fetchReport = async (range) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(range?.start_date || values.start_date);
      const formattedEndDate = formatPayloadDate(range?.end_date || values.end_date);
      const payload = { ...values, destination_number: values?.destination_number || undefined };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;
      // Required by the API — a missing archiveId is answered with 400.
      payload['archiveId'] = values?.archiveId ?? 0;

      const response = await dispatch(callHistoryGet(payload)).unwrap();
      if (response) {
        setHeaderFields({
          ...headerFields,
          default: response?.header_fields?.default || [],
          crm_fields: response?.header_fields?.crm_fields || []
        });
        setReportData(response?.data || []);
        setPaginationDetails({
          ...paginationDetails,
          limit: response.limit,
          page: response.page,
          total: response.total
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const onSubmit = () => fetchReport();

  // Runs the load-time search exactly once, whichever path resolves the range first.
  const runInitialSearch = (range) => {
    if (hasAutoSearchedRef.current) return;
    hasAutoSearchedRef.current = true;
    fetchReport(range);
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        setArchiveOptions(formatArchiveOptions(response.data, timezone));
      } else {
        setArchiveOptions([]);
        // No month options means no derived range, so fall back to the default dates.
        runInitialSearch();
      }
    } catch (error) {
      setArchiveOptions([]);
      runInitialSearch();
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
  }, []);

  useEffect(() => {
    // Skip the mount pass - the initial search is fired once the archive-derived
    // range is known, so it matches the dates shown in the filter. Page/limit
    // changes after that keep paginating as before.
    if (!hasAutoSearchedRef.current) return;
    onSubmit();
  }, [values?.limit, values?.page]);

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  // The archive options land after mount, so the month range is only known here.
  // This module opens on today, so the first pass keeps the default today range and
  // only fires the initial search; later month changes still snap the filter to the
  // selected month's range.
  useEffect(() => {
    if (minDate && maxDate) {
      if (!hasAutoSearchedRef.current) {
        runInitialSearch();
        return;
      }
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
    } else if (archiveOptions?.length > 0) {
      // Options loaded but the label gave no range - do not leave the first search pending.
      runInitialSearch();
    }
  }, [minDate?.getTime(), maxDate?.getTime(), archiveOptions?.length]);

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return { headers: csvHeaderData, data, filename: 'Call_History.csv' };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = { ...values, destination_number: values?.destination_number || undefined };
      // Export must cover every matching record, not just the page currently on screen.
      delete payload.page;
      delete payload.limit;
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;
      payload['archiveId'] = values?.archiveId ?? 0;

      const response = await dispatch(callHistoryGet(payload)).unwrap();
      setIsExporting(false);

      if (response) {
        const combinedHeaders = [
          ...(response?.header_fields?.default || []),
          ...(response?.header_fields?.crm_fields || [])
        ];

        const headers = combinedHeaders.map((val) => ({ label: val?.label, key: val?.name }));

        setCsvHeaderData(headers);
        setCsvReportData(
          maskExportRows(response?.data || [], MASKED_NUMBER_FIELDS, isNumberMaskingEnabled)
        );
        setIsCsvDownload(true);
      }
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title={t('callHistory')}
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={reportData?.length > 0}
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label={t('fromTime')}
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label={t('toTime')}
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormInput
                  label={t('destinationNumber')}
                  placeholder={t('enterDestinationNumber')}
                  value={values.destination_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="destination_number"
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label={t('type')}
                  placeholder="Select Type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="type"
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Answered', value: 'answered' },
                    { label: 'Unanswered', value: 'not_answered' }
                  ]}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit" disabled={isLoading}>
                {t('search')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.default?.length}>{t('default')}</th>
                </tr>
                <tr>
                  {headerFields?.default?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.default?.map((value, idx) => {
                        if (value?.name === 'recording_file') {
                          return (
                            <td key={idx} className="text-center cursor-pointer">
                              {val[value?.name] ? (
                                <Icon.Download
                                  size={16}
                                  onClick={() => {
                                    const link = `${config.BASE_URL}cdrs_report/download?filename=${val[value?.name]}`;
                                    fileDownload(link);
                                  }}
                                />
                              ) : (
                                '-'
                              )}
                            </td>
                          );
                        }
                        if (MASKED_NUMBER_FIELDS.includes(value?.name)) {
                          return (
                            <td key={idx}>
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                {getMaskedValue(val[value?.name], isNumberMaskingEnabled)}{' '}
                                <div className="d-flex align-items-center gap-2">
                                  <Icon.PhoneForwarded
                                    size={14}
                                    className={`text-green ${!val[value?.name]
                                      ? 'callex-disabled cursor-not-allowed'
                                      : 'cursor-pointer'
                                      }`}
                                    onClick={() => {
                                      if (!val[value?.name]) return;
                                      dispatch(setIsDialNumber(val[value?.name]));
                                      dispatch(setCallingModalOpen(true));
                                    }}
                                  />
                                  {value?.name === 'crm_number' && user?.userWhatsappPermission && (
                                    <img
                                      src={whatsappIcon}
                                      width={16}
                                      height={16}
                                      className={`${!val[value?.name]
                                        ? 'callex-disabled cursor-not-allowed'
                                        : 'cursor-pointer'
                                        }`}
                                      onClick={() => {
                                        if (!val[value?.name]) return;
                                        handleWhatsappClick(val[value?.name]);
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.crm_fields?.length}>{t('crm')}</th>
                </tr>
                <tr>
                  {headerFields?.crm_fields?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.crm_fields?.map((value, idx) => {
                        if (MASKED_NUMBER_FIELDS.includes(value?.name)) {
                          return (
                            <td key={idx}>
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                {getMaskedValue(val[value?.name], isNumberMaskingEnabled)}{' '}
                                <div className="d-flex align-items-center gap-2">
                                  <Icon.PhoneForwarded
                                    size={14}
                                    className={`text-green ${!val[value?.name]
                                      ? 'callex-disabled cursor-not-allowed'
                                      : 'cursor-pointer'
                                      }`}
                                    onClick={() => {
                                      if (!val[value?.name]) return;
                                      dispatch(setIsDialNumber(val[value?.name]));
                                      dispatch(setCallingModalOpen(true));
                                    }}
                                  />
                                  {value?.name === 'crm_number' && user?.userWhatsappPermission && (
                                    <img
                                      src={whatsappIcon}
                                      width={16}
                                      height={16}
                                      className={`${!val[value?.name]
                                        ? 'callex-disabled cursor-not-allowed'
                                        : 'cursor-pointer'
                                        }`}
                                      onClick={() => {
                                        if (!val[value?.name]) return;
                                        handleWhatsappClick(val[value?.name]);
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-65px' }}>
            <Pagination
              totalCount={paginationDetails?.total}
              limit={paginationDetails?.limit}
              page={paginationDetails?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          {t('noDataAvailable')}
        </Card>
      )}
    </div>
  );
};

export default CallHistory;
